/* eslint-disable camelcase */
import React, { FC, memo, useCallback, useEffect, useMemo, useState } from "react"
import _ from "lodash"
import moment from "moment"
import numeral from "numeral"
import {
  ActivityIndicator,
  Appearance,
  ListRenderItem,
  ListRenderItemInfo,
  SectionListData, TouchableOpacity,
  useWindowDimensions,
  ViewStyle,
} from "react-native"
import { Box, Divider, Icon, Skeleton, useDisclose, View } from "native-base"
import { IViewProps } from "native-base/lib/typescript/components/basic/View/types"
import {
  Button,
  JalaIcon,
  JalaLoading,
  JalaWeatherIcon,
  LazyRender,
  ListEmpty,
  Screen,
  SectionList,
  Text,
} from "../../../../components"
import { SafeAreaView } from "react-native-safe-area-context"
import { TextPresets } from "../../../../components/text/text.presets"
import { TextProps } from "../../../../components/text/text.props"
import { color, nativeBaseTheme } from "../../../../theme"
import { navigate } from "../../../../navigators"
import { observer } from "mobx-react-lite"
import { translate } from "../../../../i18n"
import { useStores } from "../../../../models"
import FontAwesomeIcon from "react-native-vector-icons/FontAwesome"
import { delay } from "../../../../utils/delay"
import { DateTimeFilter } from "../../../../components/date-time-filter/date-time-filter"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  flex: 1
}

const CONTENT_CONTAINER: ViewStyle = {
  // flexGrow: 1,
  paddingBottom: 68
}

const FILTER_BUTTON: ViewStyle = {
  flex: 0.40,
  borderTopLeftRadius: 60,
  borderBottomLeftRadius: 60,
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  backgroundColor: color.palette.blueAzure
}

const ADD_BUTTON: ViewStyle = {
  flex: 0.60,
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  borderTopRightRadius: 60,
  borderBottomRightRadius: 60,
  backgroundColor: color.palette.blueJalaDark
}

export const Cell: FC<{
  colorScheme?: "light" | "dark",
  text?: string,
  textPreset?: TextPresets,
  textProps?: TextProps,
  textColor?: TextProps['color']
} & IViewProps> = memo(observer(function TableHead(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    text = '-', textPreset = "body2", textProps, textColor,
    children,
    flex = 1, paddingX = "2px", paddingY = "10px", justifyContent = "center", alignItems="center", width, ...rest
  } = props;

  return <View
    flex={!width && flex || undefined}
    paddingX={paddingX}
    paddingY={paddingY}
    justifyContent={justifyContent}
    alignItems={alignItems}
    width={width}
    {...rest}
  >
    {children || <Text
      text={text}
      preset={textPreset}
      colorScheme={colorScheme}
      color={textColor}
      isTruncated
      {...textProps}
    />}
  </View>
}))

export const FloatButton: FC<{
  colorScheme?: "light" | "dark",
  dateTimeFilterValue?: {
    date_after?: string,
    date_before?: string,
    time?: string
  },
  onDateTimeFilterChange?: (value: {
    date_after?: string,
    date_before?: string,
    time?: string
  }) => void,
  onAddPress?: () => void,
  withTime?: boolean,
  prepend?: React.ReactElement
}> = ({
  colorScheme = "light",
  dateTimeFilterValue = {},
  onDateTimeFilterChange,
  onAddPress,
  withTime = false,
  prepend = null
}) => {
  const { isOpen, onOpen, onClose } = useDisclose(false)

  useEffect(() => {
    if (isOpen) JalaAnalytic.log('Mobile app: pond - filter');
  }, [isOpen])

  return (
    <>
      <View position="absolute" right={0} bottom={0} left={0}>
        {prepend}
        <View
          flex={1}
          backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
          flexDirection="row" marginX="12px" marginBottom="12px" borderRadius="60px" shadow="4" zIndex={6}
        >
          <Button
            testID="jala:id/filter-button"
            preset="primary"
            colorScheme={colorScheme}
            style={FILTER_BUTTON}
            onPress={onOpen}
          >
            <View alignSelf="stretch" flexDirection="row" alignItems="center">
              <Icon as={FontAwesomeIcon} name="filter" size="24px" marginY="2px" marginLeft="18px" marginRight="8px" color={color.palette.white} />
              <Text tx="Filter" preset="title3" color={color.palette.white} />
              {!_.isEmpty(dateTimeFilterValue) && <View rounded="full" size="8px" backgroundColor="red.500" alignSelf="center" mt="-8px" />}
            </View>
          </Button>
          <Button
            testID="jala:id/filter-button"
            preset="primary"
            colorScheme={colorScheme}
            style={ADD_BUTTON}
            onPress={onAddPress}
          >
            <View alignSelf="stretch" flexDirection="row" alignItems="center">
              <Icon as={JalaIcon} name="add" size="24px" marginLeft="10px" marginRight="8px" color={color.palette.white} />
              <Text tx="Record" preset="title3" color={color.palette.white} />
            </View>
          </Button>
        </View>
        <SafeAreaView edges={["bottom"]} />
      </View>
      <DateTimeFilter
        colorScheme={colorScheme}
        actionsheetProps={{
          title: 'Filter'
        }}
        value={dateTimeFilterValue}
        onValueChange={onDateTimeFilterChange}
        withTime={withTime}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  )
}

const TableHead: FC<{ colorScheme?: "light" | "dark" }> = observer(function TableHead(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;

  const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });

  const renderPlaceholder = useCallback(() => {
    return <Skeleton.Text lines={1} paddingX="8px" paddingY="10px" />
  }, [])

  return <View
    borderWidth="1px"
    borderColor={`gray.${colorScheme === 'light' ? '200' : '800'}`}
    backgroundColor={`gray.${colorScheme === 'light' ? '100' : '900'}`}
    flexDirection="row"
    zIndex={10}
  >
    <LazyRender renderPlaceholder={renderPlaceholder}>
      <Cell text={_.upperFirst(translate('multiMeasurements.temperature_short'))} textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('multiMeasurements.do_short'))} textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('multiMeasurements.salinity_short'))} textPreset="body3" colorScheme={colorScheme} />
      <Cell text={translate('multiMeasurements.ph')} textPreset="body3" colorScheme={colorScheme} />
      <Cell text={translate('multiMeasurements.orp_short')} textPreset="body3" colorScheme={colorScheme} />
      <Cell paddingY="0px">
        <Icon {...iconBaseStyle} as={JalaIcon} name="kecerahan" size="18px" color="primary.500" />
      </Cell>
      <Cell width="32px" paddingY="0px">
        <Icon {...iconBaseStyle} as={JalaIcon} name="water-color" size="18px" color="primary.500" />
      </Cell>
      <Cell width="32px" paddingY="0px">
        <Icon {...iconBaseStyle} as={JalaIcon} name="weather-sunny-cloudy" size="18px" color="primary.500" />
      </Cell>
      <Cell text={_.upperFirst(translate('edit'))} width="48px" padding="0px" textPreset="body3" colorScheme={colorScheme} />
    </LazyRender>
  </View>;
});

const SectionHeader: FC<{ colorScheme?: "light" | "dark", info: { section: SectionListData<any, any> } }> = observer(function SectionHeader(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info } = props;
  const { section: { title: { age, measured_date } } } = info;

  const renderPlaceholder = useCallback(() => {
    return <View height="40px"><Skeleton.Text width="30%" lines={1} /></View>
  }, [])

  return <View height="40px" backgroundColor={colorScheme === "light" ? color.palette.white : nativeBaseTheme.colors.dark["50"]}>
    <View padding="8px">
      <LazyRender renderPlaceholder={renderPlaceholder}>
        <Text
          text={`(${age}) ${moment(measured_date).format('dddd, DD MMM YYYY')}`}
          preset="title4"
          colorScheme={colorScheme}
          color={`muted.${colorScheme === 'light' ? '700' : '300'}`}
        />
      </LazyRender>
    </View>
    <Divider />
  </View>
})

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any>, handleEdit: (item: any) => void }> = observer(function Item(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info, handleEdit } = props;
  const {
    item,
    item: {
      measured_at,
      temperature, temperature_anomaly,
      do: dissolvedOxygen, do_anomaly,
      salinity, salinity_anomaly,
      ph, ph_anomaly, orp, orp_anomaly,
      transparency, transparency_anomaly,
      color, weather,
      _pushing,
      modified
    }
  } = info;

  const transformText = useCallback((text) => (
    text.replace(/\w+/g, _.upperFirst)
  ), [])

  const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });
  const measuredAt = moment(measured_at);
  const iconName = moment(measuredAt.format('HH:mm:ss'), 'HH:mm:ss')
    .isBefore(moment('12:00:00', 'HH:mm:ss')) ? 'day' : 'night'

  const renderPlaceholder = useCallback(() => {
    return <View height="74px">
      <Skeleton.Text lines={1} width="20%" paddingTop="8px" paddingX="8px" _line={{ height: '12px' }} />
      <Skeleton.Text lines={1} padding="8px" _line={{ height: '14px' }} />
    </View>
  }, [])

  const onPressEdit = useCallback(() => handleEdit(item), [handleEdit, item])

  const valueFormatted = (value, format = '0,0.[00]') => (
    value != null && numeral(value).format(format) || '-'
  )

  const textColor = (anomaly) => anomaly
    ? `danger.${colorScheme === 'light' ? '600' : '400'}`
    : `primary.${colorScheme === 'light' ? '700' : '300'}`

  return <LazyRender renderPlaceholder={renderPlaceholder}>
    <View height="74px">
      <View flexDirection="row" paddingTop="8px" paddingX="8px" alignItems="center">
        <Icon
          {...iconBaseStyle}
          as={JalaIcon}
          name={iconName}
          size="12px"
          color={`muted.${colorScheme === 'light' ? '700' : '300'}`}
        />
        <Text text=" " />
        <Text
          text={measuredAt.format('hh:mm A')}
          transformText={transformText}
          preset="body3"
          colorScheme={colorScheme}
          color={`muted.${colorScheme === 'light' ? '700' : '300'}`}
        />
      </View>
      <View flexDirection="row" position="relative">
        <Cell text={valueFormatted(temperature, '0,0.[0]')} colorScheme={colorScheme} textColor={textColor(temperature_anomaly)} />
        <Cell text={valueFormatted(dissolvedOxygen)} colorScheme={colorScheme} textColor={textColor(do_anomaly)} />
        <Cell text={valueFormatted(salinity, '0,0.[0]')} colorScheme={colorScheme} textColor={textColor(salinity_anomaly)} />
        <Cell text={valueFormatted(ph)} colorScheme={colorScheme} textColor={textColor(ph_anomaly)} />
        <Cell text={valueFormatted(orp, '0,0.[0]')} colorScheme={colorScheme} textColor={textColor(orp_anomaly)} />
        <Cell text={valueFormatted(transparency, '0,0')} colorScheme={colorScheme} textColor={textColor(transparency_anomaly)} />
        <Cell width="32px" paddingY="0px" colorScheme={colorScheme}>
          {color && <Box backgroundColor={`#${Number(color).toString(16)}`} size="12px" borderRadius="4px" />}
        </Cell>
        <Cell width="32px" paddingY="0px" colorScheme={colorScheme}>
          {weather && <JalaWeatherIcon weatherId={weather} size="18px" color="primary.500" colorScheme={colorScheme} />}
        </Cell>
      <Cell width="48px" paddingY="0px" justifyContent="center" alignItems="center" colorScheme={colorScheme}>
        {_pushing
          && <ActivityIndicator size={15} />
          || <TouchableOpacity onPress={onPressEdit}>
            <View size="32px" justifyContent="center" alignItems="center">
              <Icon as={JalaIcon} name="pencil" size="12px" color="primary.500"/>
            </View>
        </TouchableOpacity>}
      </Cell>
        {modified && (
          <Cell
            width="10px" right="0px" top="5px" position="absolute" paddingY="0px"
            justifyContent="center" colorScheme={colorScheme} backgroundColor="blue.200"
          >
            <Icon {...iconBaseStyle} as={JalaIcon} name="cloud-upload" size="10px" color="primary.500" />
          </Cell>
        )}
      </View>
    </View>
  </LazyRender>;
})

export const CycleWaterQualityMultiMeasurementListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function MeasurementScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const {
    appStore,
    appStore: {
      isOffline,
      farmStore: {
        pondStore: {
          cycleStore,
          cycleStore: {
            cycle,
            waterQualityMultiMeasurementsPerDate: models,
            loadingWaterQualityMultiMeasurement: loadingModel
          }
        }
      }
    },
    waterQualityMultiMeasurementStore: modelStore,
    waterQualityMultiMeasurementStore: { pushIdQueue, needRefresh, storing, updating, deleting, modifying },
  } = useStores()

  const [dateTimeFilter, setDateTimeFilter] = useState<{
    date_after?: string,
    date_before?: string,
    time?: string
  }>({})

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: pond - view tabs - measurement');
  }, []))

  useEffect(() => {
    modelStore.init();
    cycleStore.getWaterQualityMultiMeasurements(!isOffline).catch();
  }, [cycleStore, isOffline, modelStore])

  useEffect(() => {
    if (needRefresh) {
      if (isOffline) {
        cycleStore.getWaterQualityMultiMeasurements(false).then(() => {
          modelStore.set('needRefresh', null)
          modelStore.set('pushIdQueue', {})
        })
      } else {
        cycleStore.getWaterQualityMultiMeasurements(false).then(() => {
          cycleStore.getWaterQualityMultiMeasurements(true, () => {
            modelStore.set('needRefresh', null)
            modelStore.set('pushIdQueue', {})
          }).catch()
        })
      }
    }
  }, [cycleStore, needRefresh, modelStore, isOffline])

  const modelSections = useMemo(() => {
    return models.filter(({ measured_date }) => (
        (!dateTimeFilter.date_after || dateTimeFilter.date_after <= measured_date)
        && (!dateTimeFilter.date_before || measured_date <= dateTimeFilter.date_before)
      ))
      .map(({ measured_date, age, multi_measurements }) => ({
        title: { measured_date, age },
        data: multi_measurements.filter(({ measured_time }) => (
          !dateTimeFilter.time
          || (dateTimeFilter.time === 'am' && (measured_time >= '00:00:00' && measured_time < '12:00:00'))
          || (dateTimeFilter.time === 'pm' && (measured_time >= '12:00:00' && measured_time < '24:00:00'))
        )).map((m) => ({
          ...m,
          _pushing: !!pushIdQueue[m.id],
          loading: loadingModel
        }))
      }))
  }, [dateTimeFilter, loadingModel, models, pushIdQueue])

  const onAddPress = useCallback(async () => {
    JalaAnalytic.log('Mobile app: pond - add measurement');
    await delay(1);
    cycle && navigate('Home.Stack', 'Home.WaterQualityMultiMeasurement.Create', { pond_id: cycle.pond_id, cycle_id: cycle.id })
  }, [cycle])

  const handleEdit = useCallback(async (item) => {
    await delay(1);
    navigate('Home.Stack', 'Home.WaterQualityMultiMeasurement.Edit', { ...item })
  }, [])

  const renderSectionHeader = useCallback((info) => (
    <SectionHeader info={info} colorScheme={colorScheme} />
  ), [colorScheme])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} handleEdit={handleEdit} />
  }, [colorScheme, handleEdit])

  const keyExtractor = useCallback((item, index) => `${item.id || item.measured_date || index}`, [])

  const onRefresh = useCallback(() => {
    appStore.checkNetworkConnection().then(() => {
      cycleStore.getWaterQualityMultiMeasurements(!isOffline).catch()
    }).catch()
  }, [appStore, cycleStore, isOffline])

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      {!!modelSections.length && <TableHead colorScheme={colorScheme} />}
      <SectionList
        sections={modelSections}
        renderSectionHeader={renderSectionHeader}
        renderItem={renderItem}
        contentContainerStyle={CONTENT_CONTAINER}
        keyExtractor={keyExtractor}
        ListEmptyComponent={(loadingModel || modifying) && (
          <View flexGrow={1} alignItems="center" justifyContent="center" height={`${height - 214}px`}>
            <JalaLoading />
          </View>
        ) || <ListEmpty text={translate("listEmpty.text")} colorScheme={colorScheme} height={`${height - 214}px`} />}
        ListHeaderComponent={(loadingModel || storing) && !!modelSections.length && !updating && !deleting && (
          <View height="40px" alignItems="center" justifyContent="center" paddingTop="8px">
            <Text tx="fetchingData" preset="body2" colorScheme={colorScheme} color={nativeBaseTheme.colors.muted[colorScheme === 'light' ? '700' : '300']} />
          </View>
        ) || undefined}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={false}
        estimatedItemSize={74}
      />
      {!cycle.finished_at && <FloatButton
        colorScheme={colorScheme}
        dateTimeFilterValue={dateTimeFilter}
        withTime
        onDateTimeFilterChange={setDateTimeFilter}
        onAddPress={onAddPress}
      />}
    </Screen>
  )
})
